import React from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';

import {getPercent, stringFormat} from '@ey/utils';
import {getTheme} from '@ey/styled-theme';

import {labels} from '../../../../../util/utils';
import Utility from '../../../../../util/utilityfunctions';
import {dashboardTabId, milestonesFilterId, projectSessionKeys} from '../../../../../util/uiconstants';
import normalizeDashboardFilters from '../../../../../util/dashboard/normalizeDashboardFilters';

import EmptyMessage from '../../../../Common/Messages/EmptyMessage';
import MetricsTile from '../../../../Common/Tile/MetricsTile';
import MetricsTileLabel from '../../../../Common/Tile/MetricsTileLabel';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../actions/Metrics/dashboardFiltersAction';

const MilestonesMetric = ({engagementMetrics}) => {
	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);

	const dispatch = useDispatch();

	const currentTheme = getTheme(Utility.isDarkMode());

	const hasMetrics = engagementMetrics && engagementMetrics.length !== 0;

	const getMetricTitleLabel = (context, countProp, overdueProp, title, color, filterTitle) => {
		let engagementsCount = context?.length;
		let count = context?.filter((x) => x[countProp]).length;
		let countOverdue = context?.filter((x) => x[overdueProp]).length;
		let content = stringFormat(
			labels.outOfEngagements,
			Utility.getFormattedNumber(numberFormat, count),
			Utility.getFormattedNumber(numberFormat, engagementsCount)
		);

		const handlerOnGraphicClick = () => {
			const engagements = context?.filter((x) => x[countProp]).map(({engId}) => engId);
			const handlerOnClearFilter = () => {
				dispatch(clearFilterDashboardTiles(dashboardTabId.milestones));
			};

			const dashboardFilters = normalizeDashboardFilters(
				[
					{
						categoryLabel: filterTitle,
						type: milestonesFilterId.keyEvents,
						onClearChip: handlerOnClearFilter
					}
				],
				engagements,
				handlerOnClearFilter
			);

			dispatch(updateFilterDashboardTiles(dashboardTabId.milestones, dashboardFilters));
		};

		let result = {
			label: (
				<MetricsTileLabel
					title={title}
					content={content}
					warningText={
						countOverdue > 0 ? `${Utility.getFormattedNumber(numberFormat, countOverdue)} ${labels.overdue}` : ''
					}
				/>
			),
			value: `${
				engagementsCount === 0 ? 0 : Utility.getFormattedNumber(numberFormat, getPercent(count, engagementsCount))
			}%`,
			valueColor: color,
			onGraphicClick: handlerOnGraphicClick
		};

		return result;
	};

	const getPartnerInChargeInvitedToTheEngagement = (context) => {
		return getMetricTitleLabel(
			context,
			'partInv',
			'isPartInvDlyd',
			labels.partnerInChargeInvitedToTheEngagement,
			currentTheme.color[`grey500`],
			labels.picInvited
		);
	};

	const getPartnerInChargeSignedOffOnCraSummary = (context) => {
		let content = '';

		return getMetricTitleLabel(
			context,
			'hasCRAPICSO',
			'isPartCRASODlyd',
			labels.partnerInChargeSignedOffOnCraSummary,
			currentTheme.color[`grey500`],
			labels.picSignOffCRA
		);
	};

	const getEqrSignedOffOnCraSummary = (context) => {
		let content = '';
		return getMetricTitleLabel(
			context,
			'hasCRAEQRSO',
			'isEQRCRASODlyd',
			labels.eqrSignedOffOnCraSummary,
			currentTheme.color[`grey500`],
			labels.eqrSingOffCRA
		);
	};

	const getPartnerInChargeSignedOffOnAuditPlan = (context) => {
		return getMetricTitleLabel(
			context,
			'hasAPPICSO',
			'isPartAPSODlyd',
			labels.partnerInChargeSignedOffOnAuditPlan,
			currentTheme.color[`grey500`],
			labels.picSignOffAuditPlan
		);
	};

	const items = [
		getPartnerInChargeInvitedToTheEngagement(engagementMetrics),
		getPartnerInChargeSignedOffOnCraSummary(engagementMetrics),
		getPartnerInChargeSignedOffOnAuditPlan(engagementMetrics),
		getEqrSignedOffOnCraSummary(engagementMetrics)
	];

	return (
		<MetricsTile
			title={labels.keyEventCompletion}
			items={items}
			showMessage={!hasMetrics}
			noResultFoundMessage={
				<EmptyMessage title={labels.thereIsNoDataToDisplay} subTitle={labels.selectadditionalEntitiesToContinue} />
			}
		/>
	);
};

MilestonesMetric.defaultProps = {
	engagementMetrics: []
};

MilestonesMetric.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default MilestonesMetric;
